import React, { useMemo } from 'react';
import classnames from 'classnames';
import ReactTooltip from 'react-tooltip';
import shortid from 'shortid';
import { map, head, isEmpty, find, castArray, filter, size } from 'lodash';

// local dependencies
import { textWithPatternMatch } from 'utils/textWithPatternMatch';
import { getLabel } from 'utils/getLabel';
import { BLANK_VALUE_SYMBOL } from 'constants/common';

/**
 * @desc Data cell view for single or mult-select data cells.
 * @example
 * import { deepSelect as DeepSelectCellView } from './cellViews/deepSelect.view';
 */
export const deepSelect = ( props ) => {

    // extract display values and field values
    const displayValues = map( castArray( props.value ), ( groupValue ) => {
        
        // get parent option based on `groupValue.id` and child options
        const parentOption = find( props.options, { value: groupValue.id } );
        const childOptions = isEmpty( parentOption ) ? [] : filter( map( groupValue.selection, ( childValue ) => {
            return find( parentOption.options, { value: childValue } );
        } ), Boolean );

        // construct display values
        return {
            parent: parentOption ? getLabel( parentOption.title ) : BLANK_VALUE_SYMBOL,
            children: map( childOptions, childOption => getLabel( childOption.title ) ),
        };
    } );

    // get first display value as visible display value
    const visibleDisplayValue = head( displayValues );

    // get visible display values based on `maxDisplayValues`
    const isValueTruncated = 0 < size( displayValues );
    
    // dynamic class name
    const widgetClassName = classnames( 'ui-table-cell-view-deep-select', props.className, {
        'ui-table-cell-view-deep-select--truncated': isValueTruncated,
    } );

    // unique id
    const uniqId = useMemo( shortid.generate, [] );

    return (
        <div className={ widgetClassName }>
            {/* display values */}
            <div className='ui-table-cell-view-deep-select__display-values'>
                {/* parent value */}
                <div
                    className='ui-table-cell-view-deep-select__display-values__item'
                    style={ { color: props.displayColors[ visibleDisplayValue.parent ] } }
                    dangerouslySetInnerHTML={ { __html: props.isSearchable ? textWithPatternMatch( {
                        value: visibleDisplayValue.parent,
                        pattern: props.searchQuery,
                        wildcard: false,
                    } ) : visibleDisplayValue.parent } }
                    { ...( isValueTruncated ? { 'data-tip': 'true', 'data-for': uniqId } : {} ) }
                />

                {/* child values */}
                {
                    map( visibleDisplayValue.children, ( childValue, index ) => {
                        return (
                            <div
                                key={ index }
                                className='ui-table-cell-view-deep-select__display-values__item'
                                style={ { color: props.displayColors[ childValue ] } }
                                dangerouslySetInnerHTML={ { __html: props.isSearchable ? textWithPatternMatch( {
                                    value: childValue,
                                    pattern: props.searchQuery,
                                    wildcard: false,
                                } ) : childValue } }
                                { ...( isValueTruncated ? { 'data-tip': 'true', 'data-for': uniqId } : {} ) }
                            />
                        );
                    } )
                }
            </div>

            {/* plus button */}
            {
                1 < size( displayValues ) ? (
                    <div
                        className='ui-table-cell-view-deep-select__plus-button'
                        { ...( isValueTruncated ? { 'data-tip': 'true', 'data-for': uniqId } : {} ) }
                    >
                        +{ size( displayValues ) - 1 }
                    </div>
                ) : null
            }

            {/* tooltip */}
            {
                isValueTruncated ? (
                    <ReactTooltip
                        id={ uniqId }
                        aria-haspopup='true'
                        delayShow={ 300 }
                        className='ui-table-cell-view-deep-select__tooltip'
                        backgroundColor={ '#cdd7e1' }
                    >
                        {
                            displayValues.map( ( displayValue, _index ) => {
                                return (
                                    <div key={ _index } className='ui-table-cell-view-deep-select__tooltip__values'>
                                        {/* parent value */}
                                        <p className='ui-table-cell-view-deep-select__tooltip__values__item'> { displayValue.parent } </p>

                                        {/* child values */}
                                        {
                                            map( displayValue.children, ( childValue, index ) => {
                                                return (
                                                    <p key={ index } className='ui-table-cell-view-deep-select__tooltip__values__item'> {childValue } </p>
                                                );
                                            } )
                                        }
                                    </div>
                                );
                            } )
                        }
                    </ReactTooltip>
                ) : null
            }
        </div>
    );
};

// display name
deepSelect.displayName = 'table.cellViews.deepSelect';

// default values
deepSelect.defaultValues = {
    value: [],
    displayColors: {},
    isSearchable: false,
    searchQuery: '',
};
